import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Lock, Sparkles, Save, Pencil, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  civ_membership:        { en: 'CIV Membership #',     es: 'N° Membresía CIV' },
  rif:                   { en: 'RIF',                  es: 'RIF' },
  business_registration: { en: 'Business Registration', es: 'Registro Mercantil' },
  cedula:                { en: 'Cédula ID',            es: 'Cédula' },
  colegio_abogados_number: { en: 'Bar Association #',   es: 'N° Colegio de Abogados' },
  colegio_abogados_name:   { en: 'Bar Association Name', es: 'Nombre del Colegio' },
  inpreabogado_number:     { en: 'INPREABOGADO #',       es: 'N° INPREABOGADO' },
};

export default function VerificationDataCard({ targetUserId = null, mode = 'self', refreshKey = 0 }) {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const isSuperadmin = user?.role === 'superadmin';
  const path = mode === 'self'
    ? `${API}/verification/data/me`
    : `${API}/verification/data/${targetUserId}`;

  const load = () => {
    setLoading(true);
    axios.get(path, { headers })
      .then(r => { setData(r.data); setDraft(r.data.data || {}); })
      .catch(() => setData({ fields: [], data: {} }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (mode === 'admin' && !targetUserId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, refreshKey]);

  if (loading) {
    return (
      <Card data-testid="vdata-loading">
        <CardContent className="p-5 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 mx-auto animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.fields || data.fields.length === 0) return null;

  const fields = data.fields;
  const values = data.data || {};
  const hasAny = fields.some(f => values[f]);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { data: {} };
      fields.forEach(f => { payload.data[f] = (draft[f] || '').trim(); });
      const r = await axios.put(`${API}/verification/data/${targetUserId}`, payload, { headers });
      setData(d => ({ ...d, data: r.data.data }));
      setEditing(false);
      toast.success(tt('Verification data updated', 'Datos de verificación actualizados'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-[#D4AF37]/30 bg-gradient-to-br from-amber-50/40 to-white" data-testid="verification-data-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-slate-900 text-base flex items-center gap-2">
                {tt('Verification Data', 'Datos de Verificación')}
                <Lock className="w-3.5 h-3.5 text-slate-400" />
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {mode === 'self'
                  ? tt('Private — only visible to you and the Super Admin team. Auto-extracted by AI from your verification documents.',
                      'Privado — solo visible para ti y el equipo Super Admin. Extraído por IA de tus documentos de verificación.')
                  : tt('Private — only visible to the user and Super Admin. Auto-extracted by AI; editable here.',
                      'Privado — solo visible para el usuario y Super Admin. Extraído por IA; editable aquí.')}
              </p>
              {data.ai_extracted_at && (
                <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5" />
                  {tt('Last AI extraction', 'Última extracción IA')}: {new Date(data.ai_extracted_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          {mode === 'admin' && isSuperadmin && !editing && (
            <div className="flex gap-1.5">
              <Button size="sm" variant="ghost" onClick={load} className="gap-1 h-8 text-xs" data-testid="vdata-refresh-btn" title={tt('Refresh', 'Recargar')}>
                <Loader2 className="w-3 h-3" />{tt('Refresh', 'Recargar')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="gap-1.5 h-8 text-xs"
                data-testid="vdata-edit-btn">
                <Pencil className="w-3 h-3" />{tt('Edit', 'Editar')}
              </Button>
            </div>
          )}
        </div>

        {!hasAny && !editing && (
          <div className="text-center py-4 text-sm text-slate-400 bg-slate-50 rounded-lg">
            {tt('No data extracted yet. Once you submit and the AI runs, fields will auto-populate.',
                'Sin datos aún. Tras enviar y ejecutar la IA, los campos se completarán automáticamente.')}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map(f => {
            const lbl = FIELD_LABELS[f] || { en: f, es: f };
            const val = editing ? (draft[f] ?? values[f] ?? '') : (values[f] || '');
            return (
              <div key={f} data-testid={`vdata-field-${f}`}>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{tt(lbl.en, lbl.es)}</Label>
                {editing ? (
                  <Input
                    value={val}
                    onChange={e => setDraft(d => ({ ...d, [f]: e.target.value }))}
                    placeholder={tt(lbl.en, lbl.es)}
                    className="mt-1 h-10 bg-white"
                    data-testid={`vdata-input-${f}`}
                  />
                ) : (
                  <div className="mt-1 px-3 py-2 bg-white rounded-md border border-slate-200 text-sm font-medium text-slate-800 min-h-[2.5rem] flex items-center" data-testid={`vdata-value-${f}`}>
                    {val || <span className="text-slate-300 italic font-normal">{tt('Not extracted', 'No extraído')}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {editing && (
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(values); }} disabled={saving}
              data-testid="vdata-cancel-btn">
              {tt('Cancel', 'Cancelar')}
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving}
              className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5"
              data-testid="vdata-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {tt('Save', 'Guardar')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
